import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { toUtcIsoDate } from '../utils/date-utils';

export interface AssignLotNumDialogData {
  purchaseOrderProductId: number;
  purchaseOrderId: number;
  purchaseOrderNumber?: string;
  productId: number;
  productName?: string;
  catalogNum?: string;
  quantity: number;
  unitType?: string;
  vendorId?: number;
  vendorName?: string;
  vendorCatalogNum?: string;
  vendorLotNum?: string;
}

export interface AssignLotNumDialogResult {
  assigned: true;
}

@Component({
  standalone: true,
  selector: 'app-assign-lot-num-dialog',
  templateUrl: './assign-lot-num-dialog.component.html',
  styleUrl: './assign-lot-num-dialog.component.css',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule
  ]
})
export class AssignLotNumDialogComponent implements OnInit {
  form!: FormGroup;

  // Product autocomplete
  productSearch = new FormControl('');
  allProducts: any[] = [];
  filteredProducts!: Observable<any[]>;
  selectedProduct: any = null;

  // Vendor autocomplete
  vendorSearch = new FormControl('');
  allVendors: any[] = [];
  filteredVendors!: Observable<any[]>;
  selectedVendor: any = null;

  unitTypes: string[] = [];
  activityTypes: any[] = [];
  isSaving = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AssignLotNumDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssignLotNumDialogData,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef

  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      lotNumber:        ['', Validators.required],
      quantityReceived: [this.data.quantity ?? 0, [Validators.required, Validators.min(0)]],
      unitType:         [this.data.unitType || '', Validators.required],
      concentration:    [null],
      activityType:     [''],
      unitPrice:        [0, [Validators.required, Validators.min(0)]],
      expirationDate:   [null],
      manufacturingDate:[null],
      notes:            [''],
      vendorCatalogNum: [this.data.vendorCatalogNum || '', Validators.required],
      vendorLotNum:     [this.data.vendorLotNum || '']
    });

//TODO CLEAN UP dont need to load all products/vendors or have the search box if we are just going to assign the current ones and not allow changing them
// *****************************

    // Pre-fill the product search with the current product
    if (this.data.catalogNum) {
      this.productSearch.setValue(this.data.catalogNum);
    }

    // Load all products for the autocomplete
    this.apiService.get<any[]>('product/all').subscribe({
      next: (data) => {
        this.allProducts = (data || []).map(p => ({
          id: p.id || p.Id,
          name: p.name || p.Name || '',
          catalogNum: p.catalogNum || p.CatalogNum || ''
        }));

        // Pre-select the current product
        const current = this.allProducts.find(p => p.id === this.data.productId);
        if (current) {
          this.selectedProduct = current;
          this.productSearch.setValue(current.catalogNum);
              this.cdr.detectChanges();
        }
      },
      error: (err) => console.error('Error loading products:', err)
    });

    this.filteredProducts = this.productSearch.valueChanges.pipe(
      startWith(this.productSearch.value || ''),
      map(value => this._filterProducts(value || ''))
    );

    // Load all vendors for the autocomplete
    this.apiService.get<any[]>('vendor/all').subscribe({
      next: (data) => {
        this.allVendors = (data || []).map(v => ({
          id: v.id || v.Id,
          name: v.name || v.Name || ''
        }));

        // Pre-select the PO's vendor
        if (this.data.vendorId) {
          const current = this.allVendors.find(v => v.id === this.data.vendorId);
          if (current) {
            this.selectedVendor = current;
            this.vendorSearch.setValue(current.name);
            this.cdr.detectChanges();

          }
        }
      },
      error: (err) => console.error('Error loading vendors:', err)
    });

    this.filteredVendors = this.vendorSearch.valueChanges.pipe(
      startWith(this.vendorSearch.value || ''),
      map(value => this._filterVendors(value || ''))
    );
    // Load unit types for the select dropdown
    this.apiService.get<string[]>('product/unitTypes').subscribe({
      next: (data) => {
        this.unitTypes = data || [];
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading unit types:', err)
    });

    // Load activity types for the select dropdown
    this.apiService.get<any[]>('product/activityTypes').subscribe({
      next: (data) => {
        this.activityTypes = data || [];
        this.cdr.detectChanges();
      },
      error: (err) => console.error('Error loading activity types:', err)
    });

  }

  private _filterProducts(value: string): any[] {
    if (typeof value !== 'string') return this.allProducts;
    const f = value.toLowerCase();
    return this.allProducts.filter(p =>
      p.catalogNum.toLowerCase().includes(f) ||
      p.name.toLowerCase().includes(f)
    );
  }

  displayProduct(p: any): string {
    return p ? p.catalogNum : '';
  }

  onProductSelected(p: any): void {
    this.selectedProduct = p;
    this.errorMessage = '';
  }

  clearProduct(): void {
    this.selectedProduct = null;
    this.productSearch.setValue('');
  }

  private _filterVendors(value: string): any[] {
    if (typeof value !== 'string') return this.allVendors;
    const f = value.toLowerCase();
    return this.allVendors.filter(v => v.name.toLowerCase().includes(f));
  }

  displayVendor(v: any): string {
    return v ? v.name : '';
  }

  onVendorSelected(v: any): void {
    this.selectedVendor = v;
    this.errorMessage = '';
  }

  clearVendor(): void {
    this.selectedVendor = null;
    this.vendorSearch.setValue('');
  }

  fetchLatestLotNumber(): void {
    const productId = this.data.productId;
    if (!productId) {
      this.errorMessage = 'No product selected. Please enter a lot number manually.';
      this.cdr.detectChanges();
      return;
    }

    this.apiService.get<any>(`product/${productId}/latestlotnumber`).subscribe({
      next: (response) => {
        this.form.patchValue({ lotNumber: response?.lotNumber ?? '' });
        this.errorMessage = '';
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.errorMessage = err?.error?.error || 'Unable to determine next lot number. Please enter manually.';
        console.error('Error fetching latest lot number:', err);
        this.cdr.detectChanges();
      }
    });
  }

  save(): void {
    if (this.form.invalid || !this.selectedProduct) {
      this.form.markAllAsTouched();
      if (!this.selectedProduct) {
        this.errorMessage = 'Please select a product.';
      }
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const formVal = this.form.value;
    const newLotNum = {
      id: 0,
      lotNum: formVal.lotNumber,
      quantityReceived: formVal.quantityReceived,
      expirationDate: toUtcIsoDate(formVal.expirationDate),
      manufactureDate: toUtcIsoDate(formVal.manufacturingDate),
      notes: formVal.notes || null,
      vendorLotNum: formVal.vendorLotNum || null,
      unitType: formVal.unitType,
      concentration: formVal.concentration !== null && formVal.concentration !== '' ? formVal.concentration : null,
      activityType: formVal.activityType || null,
      };

    // Step 1: Create the lot number via SyncLotNums (append-only — id=0)
    // this.apiService.put<any[]>(`product/${this.selectedProduct.id}/lotnums`, [newLotNum]).subscribe({
    //   next: (lotNums) => {
    //     // The API returns the full updated list; find the newly created one by number
    //     const created = (lotNums || []).find(
    //       (ln: any) => (ln.number || ln.Number) === formVal.lotNumber
    //     );
    //     if (!created) {
    //       this.isSaving = false;
    //       this.errorMessage = 'Lot number was created but could not be confirmed. Please refresh.';
    //       return;
    //     }
        //const newLotNumId = created.id || created.Id;

        // Step 2: Link the new lot number to the PurchaseOrderProduct line,
        // const assignPayload: any = { lotNumId: newLotNumId };
        // if (this.selectedVendor) {
        //   assignPayload['vendorId']         = this.selectedVendor.id;
        //   assignPayload['vendorCatalogNum'] = formVal.vendorCatalogNum || null;
        //   assignPayload['vendorLotNum']     = formVal.vendorLotNum || null;
        // }

        this.apiService.patch<void>(
          `purchaseorder/product/${this.data.purchaseOrderProductId}/assign-lotnum`,
          newLotNum
        ).subscribe({
          next: () => {
            this.isSaving = false;
            this.dialogRef.close({ assigned: true } as AssignLotNumDialogResult);
          },
          error: (err) => {
            this.isSaving = false;
            //this.errorMessage = 'Lot number could not be linked to the purchase order line. Please try again.';
            this.errorMessage = 'Failed to assign lot number. Make sure all required fields are filled out and try again.';
            console.error('Error assigning lot num:', err);
            this.cdr.detectChanges();

          }
        });
    //   },
    //   error: (err) => {
    //     this.isSaving = false;
    //     this.errorMessage = 'Failed to create lot number. Please try again.';
    //     console.error('Error creating lot num:', err);
    //   }
    // });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
